import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  Button,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
  Space,
  message,
  Divider,
  Alert,
  Spin,
  Popconfirm,
  Row,
  Col,
  ColorPicker,
  Collapse
} from 'antd';
import {
  PlusOutlined,
  ShoppingCartOutlined,
  GiftOutlined,
  ReloadOutlined,
  HistoryOutlined,
  BellOutlined,
  DollarOutlined,
  EditOutlined,
  DeleteOutlined,
  CrownOutlined,
  SettingOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import ARKItemSelector from '../../components/ARKItemSelector';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

interface ShopItem {
  id: number;
  name: string;
  blueprint: string;
  category: string;
  subcategory?: string;
  description?: string;
  is_mod_item: boolean;
}

interface ShopConfig {
  SellItems?: any;
  ShopItems?: any;
  Kits?: any;
  General?: any;
  Messages?: {
    AvailableKits?: string;
    BadLevel?: string;
    BoughtBeacon?: string;
    BoughtDino?: string;
    BoughtExp?: string;
    BoughtItem?: string;
    BoughtKit?: string;
    BuyCmd?: string;
    BuyKitCmd?: string;
    BuyKitUsage?: string;
    BuyUsage?: string;
    CantBuyKit?: string;
    CantGivePoints?: string;
    FoundMorePlayers?: string;
    GotPoints?: string;
    HavePoints?: string;
    HelpCmd?: string;
    HelpMessage?: string;
    InventoryIsFull?: string;
    KitCmd?: string;
    KitUsage?: string;
    KitsLeft?: string;
    KitsListFormat?: string;
    KitsListPrice?: string;
    NoKits?: string;
    NoKitsLeft?: string;
    NoPermissionsKit?: string;
    NoPermissionsStore?: string;
    NoPlayer?: string;
    NoPoints?: string;
    NotEnoughItems?: string;
    OnlyOnSpawnKit?: string;
    PointsCmd?: string;
    ReceivedPoints?: string;
    RefundError?: string;
    RidingDino?: string;
    SellCmd?: string;
    SellUsage?: string;
    Sender?: string;
    SentPoints?: string;
    ShopCmd?: string;
    ShopFindCmd?: string;
    ShopFindNotFound?: string;
    ShopFindTooManyResults?: string;
    ShopFindUsage?: string;
    ShopMessage?: string;
    ShopSellCmd?: string;
    ShopUsage?: string;
    SoldItems?: string;
    StoreListDino?: string;
    StoreListFormat?: string;
    StoreListItem?: string;
    TradeCmd?: string;
    TradeUsage?: string;
    WrongId?: string;
  };
}

interface PurchaseRecord {
  id: number;
  timestamp: string;
  username: string;
  item: string;
  raw: string;
}

interface ShopManagementProps {
  serverId: string;
}

const ShopManagement: React.FC<ShopManagementProps> = ({ serverId }) => {
  const [loading, setLoading] = useState(false);
  const [shopConfig, setShopConfig] = useState<ShopConfig>({});
  const [vipList, setVipList] = useState<string[]>([]);
  const [purchaseRecords, setPurchaseRecords] = useState<PurchaseRecord[]>([]);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // 模态框状态
  const [addItemModalVisible, setAddItemModalVisible] = useState(false);
  const [editItemModalVisible, setEditItemModalVisible] = useState(false);
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const [batchPriceModalVisible, setBatchPriceModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null);
  
  // 表单状态
  const [addItemForm] = Form.useForm();
  const [editItemForm] = Form.useForm();
  const [rechargeForm] = Form.useForm();
  const [batchPriceForm] = Form.useForm();
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  
  
  // 编辑商品加载状态
  const [editItemLoading, setEditItemLoading] = useState(false);
  
  // 充值加载状态
  const [rechargeLoading, setRechargeLoading] = useState(false);
  
  // 批量价格设置加载状态
  const [batchPriceLoading, setBatchPriceLoading] = useState(false);

  // 新增礼包（Kits）表单与状态
  const [kitsForm] = Form.useForm();
  const [kitItemsState, setKitItemsState] = useState<any[]>([]);   // 物品条目
  const [kitDinosState, setKitDinosState] = useState<any[]>([]);   // 恐龙条目
  
  // 新增商品（Items）表单与状态
  const [itemsArrayState, setItemsArrayState] = useState<any[]>([]);   // 物品条目
  const [dinosArrayState, setDinosArrayState] = useState<any[]>([]);   // 恐龙条目
  
  // ID生成方式状态
  const [autoGenerateItemId, setAutoGenerateItemId] = useState(true);
  const [autoGenerateRecycleId, setAutoGenerateRecycleId] = useState(true);
  const [autoGenerateKitId, setAutoGenerateKitId] = useState(true);
  
  // 物品回收相关状态
  const [recycleModalVisible, setRecycleModalVisible] = useState(false);
  const [recycleForm] = Form.useForm();
  
  // 礼包相关状态
  const [addKitModalVisible, setAddKitModalVisible] = useState(false);
  
  // VIP设置相关状态
  const [vipGroups, setVipGroups] = useState<Array<{name: string, amount: number}>>([]);
  const [timedPointsEnabled, setTimedPointsEnabled] = useState(false);
  const [rewardInterval, setRewardInterval] = useState(1);
  const [stackRewards, setStackRewards] = useState(false);
  


  // 检查商店插件是否已安装
  const checkShopPlugin = useCallback(async () => {
    try {
      const response = await api.get(`/shop/${serverId}/check`);
      setIsInstalled(response.data.isInstalled);
      if (!response.data.isInstalled) {
        message.warning('请先安装商店数据包插件');
      }
    } catch (error) {
      console.error('检查商店插件失败:', error);
      message.error('检查商店插件失败');
    }
  }, [serverId]);

  // 获取商店配置
  const fetchShopConfig = useCallback(async () => {
    if (!isInstalled) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/shop/${serverId}/config`);
      setShopConfig(response.data);
      
      // 加载VIP设置
      if (response.data?.General?.TimedPointsReward) {
        const timedPoints = response.data.General.TimedPointsReward;
        setTimedPointsEnabled(timedPoints.Enabled || false);
        setRewardInterval(timedPoints.Interval || 1);
        setStackRewards(timedPoints.StackRewards || false);
        
        // 转换用户组数据
        const groups = [];
        if (timedPoints.Groups) {
          for (const [name, config] of Object.entries(timedPoints.Groups)) {
            groups.push({
              name,
              amount: (config as any).Amount || 0
            });
          }
        }
        setVipGroups(groups);
      }
    } catch (error) {
      console.error('获取商店配置失败:', error);
      message.error('获取商店配置失败');
    } finally {
      setLoading(false);
    }
  }, [isInstalled, serverId]);

  // VIP设置相关函数
  const handleAddVipGroup = () => {
    setVipGroups([...vipGroups, { name: '', amount: 0 }]);
    
    // 滚动到页面底部
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleRemoveVipGroup = (index: number) => {
    const newGroups = vipGroups.filter((_, i) => i !== index);
    setVipGroups(newGroups);
  };

  const handleVipGroupChange = (index: number, field: 'name' | 'amount', value: string | number) => {
    const newGroups = [...vipGroups];
    newGroups[index] = { ...newGroups[index], [field]: value };
    setVipGroups(newGroups);
  };

  const handleSaveVipSettings = async () => {
    try {
      setLoading(true);
      
      // 构建TimedPointsReward配置
      const groupsConfig: any = {};
      vipGroups.forEach(group => {
        if (group.name.trim()) {
          groupsConfig[group.name] = {
            Amount: Number(group.amount)
          };
        }
      });

      const timedPointsConfig = {
        Enabled: timedPointsEnabled,
        Groups: groupsConfig,
        Interval: Number(rewardInterval),
        StackRewards: stackRewards
      };

      // 更新配置
      const updatedConfig = {
        ...shopConfig,
        General: {
          ...shopConfig.General,
          TimedPointsReward: timedPointsConfig
        }
      };

      await api.put(`/shop/${serverId}/config`, { config: updatedConfig });
      message.success('VIP设置保存成功');
      fetchShopConfig(); // 重新加载配置
    } catch (error) {
      console.error('保存VIP设置失败:', error);
      message.error('保存VIP设置失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取VIP列表
  const fetchVipList = useCallback(async () => {
    if (!isInstalled) return;
    
    try {
      const response = await api.get(`/shop/${serverId}/vip-list`);
      setVipList(response.data);
    } catch (error) {
      console.error('获取VIP列表失败:', error);
    }
  }, [isInstalled, serverId]);

  // 获取购买记录
  const fetchPurchaseRecords = async (page = 1) => {
    try {
      const response = await api.get(`/shop/${serverId}/purchase-records?page=${page}&limit=20`);
      setPurchaseRecords(response.data.records);
    } catch (error) {
      console.error('获取购买记录失败:', error);
      message.error('获取购买记录失败');
    }
  };

  // 获取字段标签
  const getFieldLabel = (key: string) => {
    const labelMap: { [key: string]: string } = {
      'Description': '商品名称',
      'Price': '商品价格',
      'Type': '商品类型',
      'Amount': '包含数量',
      'Blueprint': '蓝图路径',
      'Permissions': '购买权限',
      'blueprint': '是否蓝图',
      'durability': '固定耐久',
      'armor': '固定护甲',
      'damage': '固定伤害',
      'ForceBlueprint': '强制蓝图',
      'Quality': '品质'
    };
    return labelMap[key] || key;
  };

  // 渲染表单字段
  const renderFormField = (key: string, value: any) => {
    if (key === 'Type') {
      return (
        <Select placeholder="选择商品类型">
          <Option value="item">物品</Option>
          <Option value="dino">恐龙</Option>
          <Option value="experience">经验值</Option>
          <Option value="beacon">光柱宝箱</Option>
          <Option value="command">控制台命令</Option>
          <Option value="engram">印痕解锁</Option>
        </Select>
      );
    }
    
    if (key === 'Permissions') {
      return (
        <Select
          mode="multiple"
          placeholder="选择购买权限"
          style={{ width: '100%' }}
        >
          {vipList
            .filter((vip: string) => vip && vip.trim() !== '')
            .map((vip: string) => (
              <Option key={vip} value={vip}>
                {vip}
              </Option>
            ))}
        </Select>
      );
    }
    
    if (key === 'blueprint') {
      return <Switch />;
    }
    
    if (key === 'Price' || key === 'Amount' || key === 'durability' || key === 'armor' || key === 'damage') {
      return (
        <InputNumber
          min={0}
          placeholder={`输入${getFieldLabel(key)}`}
          style={{ width: '100%' }}
          addonAfter={key === 'Price' ? '金币' : ''}
        />
      );
    }
    
    // 特殊处理Items数组
        if (key === 'Items' && Array.isArray(value)) {
          // 使用独立状态管理Items数组
          const currentItems = itemsArrayState.length > 0 ? itemsArrayState : value;
          
          // 获取所有可能的字段，用于添加新物品时的默认值
          const getAllFields = (items: any[]) => {
            const fields = new Set<string>();
            items.forEach(item => {
              if (typeof item === 'object' && item !== null) {
                Object.keys(item).forEach(key => fields.add(key));
              }
            });
            return Array.from(fields);
          };

          const allFields = getAllFields(currentItems);

          return (
            <div>
              <div style={{ marginBottom: 8 }}>
                <Button 
                  type="dashed" 
                  onClick={() => {
                    // 创建默认对象，只包含常见的字段
                    const defaultItem: any = {};
                    if (allFields.includes('Amount')) defaultItem.Amount = 1;
                    if (allFields.includes('Blueprint')) defaultItem.Blueprint = '';
                    if (allFields.includes('ForceBlueprint')) defaultItem.ForceBlueprint = false;
                    if (allFields.includes('Quality')) defaultItem.Quality = 100;
                    
                    const newItems = [...currentItems, defaultItem];
                    setItemsArrayState(newItems);
                  }}
                  style={{ width: '100%' }}
                >
                  + 添加物品
                </Button>
              </div>
              {currentItems.map((item: any, index: number) => {
                // 检查item是否为对象，如果不是则转换为对象
                const itemObj = typeof item === 'object' && item !== null ? item : {
                  [allFields[0] || 'value']: item
                };

                // 获取当前item的所有字段
                const itemFields = Object.keys(itemObj);

                // 动态渲染字段
                const renderField = (fieldKey: string, fieldValue: any) => {
                  const fieldLabel = getFieldLabel(fieldKey);
                  
                  if (typeof fieldValue === 'boolean') {
                    return (
                      <Form.Item key={fieldKey} label={fieldLabel}>
                        <Switch 
                          checked={fieldValue}
                          onChange={(checked) => {
                            const newItems = currentItems.map((item: any, i: number) => {
                              if (i === index) {
                                if (typeof item !== 'object' || item === null) {
                                  return { [fieldKey]: checked };
                                } else {
                                  return { ...item, [fieldKey]: checked };
                                }
                              }
                              return item;
                            });
                            setItemsArrayState(newItems);
                          }}
                        />
                      </Form.Item>
                    );
                  } else if (typeof fieldValue === 'number') {
                    return (
                      <Form.Item key={fieldKey} label={fieldLabel}>
                        <InputNumber 
                          min={0} 
                          style={{ width: '100%' }}
                          value={fieldValue}
                          onChange={(val) => {
                            const newItems = currentItems.map((item: any, i: number) => {
                              if (i === index) {
                                if (typeof item !== 'object' || item === null) {
                                  return { [fieldKey]: val };
                                } else {
                                  return { ...item, [fieldKey]: val };
                                }
                              }
                              return item;
                            });
                            setItemsArrayState(newItems);
                          }}
                        />
                      </Form.Item>
                    );
                  } else {
                    return (
                      <Form.Item key={fieldKey} label={fieldLabel}>
                        <Input 
                          value={fieldValue || ''}
                          onChange={(e) => {
                            const newItems = currentItems.map((item: any, i: number) => {
                              if (i === index) {
                                if (typeof item !== 'object' || item === null) {
                                  return { [fieldKey]: e.target.value };
                                } else {
                                  return { ...item, [fieldKey]: e.target.value };
                                }
                              }
                              return item;
                            });
                            setItemsArrayState(newItems);
                          }}
                        />
                      </Form.Item>
                    );
                  }
                };

                return (
                  <Card 
                    key={`item-${index}`} 
                    size="small" 
                    title={`物品 ${index + 1}`}
                    extra={
                      <Button 
                        type="text" 
                        danger 
                        size="small"
                        onClick={() => {
                          const newItems = currentItems.filter((_: any, i: number) => i !== index);
                          setItemsArrayState(newItems);
                        }}
                      >
                        删除
                      </Button>
                    }
                    style={{ marginBottom: 8 }}
                  >
                    {itemFields.map(fieldKey => renderField(fieldKey, itemObj[fieldKey]))}
                  </Card>
                );
              })}
            </div>
          );
        }
    
    // 处理其他数组类型字段
    if (Array.isArray(value)) {
      return (
        <TextArea
          rows={4}
          placeholder={`输入${getFieldLabel(key)}（JSON格式）`}
          style={{ fontFamily: 'monospace' }}
        />
      );
    }
    
    // 处理对象类型字段
    if (typeof value === 'object' && value !== null) {
      return (
        <TextArea
          rows={4}
          placeholder={`输入${getFieldLabel(key)}（JSON格式）`}
          style={{ fontFamily: 'monospace' }}
        />
      );
    }
    
    // 默认文本输入
    return <Input placeholder={`输入${getFieldLabel(key)}`} />;
  };

  // 更新商店配置
  const updateShopConfig = async (newConfig: ShopConfig) => {
    try {
      await api.put(`/shop/${serverId}/config`, { config: newConfig });
      setShopConfig(newConfig);
      message.success('商店配置更新成功');
    } catch (error) {
      console.error('更新商店配置失败:', error);
      message.error('更新商店配置失败');
    }
  };

  // 更新消息配置
  const updateMessageConfig = (key: string, value: string) => {
    setShopConfig(prevConfig => ({
      ...prevConfig,
      Messages: {
        ...prevConfig.Messages,
        [key]: value
      }
    }));
  };

  // 颜色选择器组件
  const ColorMessageInput = ({ 
    value, 
    onChange, 
    placeholder, 
    label 
  }: { 
    value: string; 
    onChange: (value: string) => void; 
    placeholder: string; 
    label: string; 
  }) => {
    const [text, setText] = useState(value || '');
    const [color, setColor] = useState('#ff0000');
    const [showColorPicker, setShowColorPicker] = useState(false);

    // 从现有文本中提取颜色
    const extractColorFromText = (text: string) => {
      const match = text.match(/Color="([^"]+)"/);
      if (match) {
        const colorStr = match[1];
        const [r, g, b, a] = colorStr.split(',').map(Number);
        return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
      }
      return '#ff0000';
    };

    // 从现有文本中提取纯文本
    const extractTextFromRichColor = (text: string) => {
      const match = text.match(/<RichColor[^>]*>(.*?)<\/RichColor>/);
      return match ? match[1] : text;
    };

    useEffect(() => {
      if (value) {
        setText(extractTextFromRichColor(value));
        setColor(extractColorFromText(value));
      }
    }, [value]);

    const handleColorChange = (color: any) => {
      const { r, g, b, a } = color.toRgb();
      const normalizedColor = `${(r / 255).toFixed(2)}, ${(g / 255).toFixed(2)}, ${(b / 255).toFixed(2)}, ${a.toFixed(2)}`;
      const richColorText = `<RichColor Color="${normalizedColor}">${text}</RichColor>`;
      onChange(richColorText);
      setColor(`rgba(${r}, ${g}, ${b}, ${a})`);
    };

    const handleTextChange = (newText: string) => {
      setText(newText);
      const currentColor = color;
      const matches = currentColor.match(/\d+/g) || ['255', '0', '0', '1'];
      const [r, g, b, a] = matches.map(Number);
      const normalizedColor = `${(r / 255).toFixed(2)}, ${(g / 255).toFixed(2)}, ${(b / 255).toFixed(2)}, ${a}`;
      const richColorText = `<RichColor Color="${normalizedColor}">${newText}</RichColor>`;
      onChange(richColorText);
    };

    return (
      <div>
        <div style={{ marginBottom: 8 }}>
          <Space>
            <span style={{ fontWeight: 500 }}>{label}</span>
            <Button 
              size="small" 
              type="dashed"
              onClick={() => setShowColorPicker(!showColorPicker)}
            >
              选择颜色
            </Button>
            {showColorPicker && (
              <ColorPicker
                value={color}
                onChange={handleColorChange}
                showText
              />
            )}
          </Space>
        </div>
        <Input.TextArea
          rows={2}
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder={placeholder}
        />
        <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
          预览: <span style={{ color: color }}>{text || placeholder}</span>
        </div>
      </div>
    );
  };

  // 保存消息配置
  const handleSaveMessages = async () => {
    try {
      setLoading(true);
      await updateShopConfig(shopConfig);
      message.success('消息配置保存成功');
    } catch (error) {
      console.error('保存消息配置失败:', error);
      message.error('保存消息配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 清空ShopItems商品
  const clearShopItems = async () => {
    try {
      const newConfig = {
        ...shopConfig,
        ShopItems: {}
      };
      
      await updateShopConfig(newConfig);
      message.success('ShopItems商品已清空');
    } catch (error) {
      console.error('清空ShopItems失败:', error);
      message.error('清空ShopItems失败');
    }
  };

  // 批量设置价格
  const batchSetPrice = async (price: number) => {
    try {
      if (!shopConfig.ShopItems || Object.keys(shopConfig.ShopItems).length === 0) {
        message.warning('ShopItems分类下没有商品');
        return;
      }

      const newConfig = {
        ...shopConfig,
        ShopItems: {} as any
      };

      // 遍历所有ShopItems商品，设置价格为用户指定的值
      Object.entries(shopConfig.ShopItems).forEach(([itemId, itemData]: [string, any]) => {
        newConfig.ShopItems[itemId] = {
          ...itemData,
          Price: price
        };
      });
      
      await updateShopConfig(newConfig);
      message.success(`已为 ${Object.keys(shopConfig.ShopItems).length} 个商品设置价格为${price}金币`);
    } catch (error) {
      console.error('批量设置价格失败:', error);
      message.error('批量设置价格失败');
    }
  };

  // 处理批量价格设置表单提交
  const handleBatchPriceSubmit = async (values: any) => {
    setBatchPriceLoading(true);
    try {
      await batchSetPrice(values.price);
      setBatchPriceModalVisible(false);
      batchPriceForm.resetFields();
    } catch (error) {
      console.error('批量设置价格失败:', error);
    } finally {
      setBatchPriceLoading(false);
    }
  };

  // 生成唯一的商品ID
  const generateUniqueItemId = () => {
    const existingIds = new Set([
      ...Object.keys(shopConfig.ShopItems || {}),
      ...Object.keys(shopConfig.SellItems || {}),
      ...Object.keys(shopConfig.Kits || {})
    ]);

    // 生成ID的策略：A001, A002, A003... 如果A系列满了，用B001, B002...
    const prefixes = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
    
    for (const prefix of prefixes) {
      for (let i = 1; i <= 999; i++) {
        const id = `${prefix}${i.toString().padStart(3, '0')}`;
        if (!existingIds.has(id)) {
          return id;
        }
      }
    }
    
    // 如果所有前缀都用完了，使用时间戳
    return `ITEM_${Date.now()}`;
  };

  // 添加商品到商店
  const addItemToShop = async (values: any) => {
    try {
      let itemId;
      
      if (autoGenerateItemId) {
        // 自动生成唯一的商品ID
        itemId = generateUniqueItemId();
      } else {
        // 使用手动输入的商品ID
        itemId = values.itemId;
        if (!itemId || !itemId.trim()) {
          message.error('请输入商品ID');
          return;
        }
        
        // 检查ID是否已存在
        const existingIds = new Set([
          ...Object.keys(shopConfig.ShopItems || {}),
          ...Object.keys(shopConfig.SellItems || {}),
          ...Object.keys(shopConfig.Kits || {})
        ]);
        
        if (existingIds.has(itemId)) {
          message.error(`商品ID "${itemId}" 已存在，请使用其他ID`);
          return;
        }
      }

      const newItem: any = {
        Description: values.description || '',
        Type: values.itemType || 'item',
        Price: values.price || 0,
        MaxLevel: values.maxLevel || 9999,
        MinLevel: values.minLevel || 1,
        Items: itemsArrayState,
        Dinos: dinosArrayState,
      };

      if (values.permissions && values.permissions.length > 0) {
        newItem.Permissions = values.permissions.join(',');
      } else {
        newItem.Permissions = '';
      }

      const newConfig = { ...(shopConfig || {}) } as any;
      if (!newConfig.ShopItems) newConfig.ShopItems = {};
      newConfig.ShopItems[itemId] = newItem;

      await updateShopConfig(newConfig);
      message.success(`商品 ${itemId} 已添加${autoGenerateItemId ? '（ID自动生成）' : ''}`);
      addItemForm.resetFields();
      setItemsArrayState([]);
      setDinosArrayState([]);
      setAddItemModalVisible(false);
    } catch (e) {
      console.error(e);
      message.error('新增商品失败');
    }
  };

  // 生成唯一的回收物品ID
  const generateUniqueRecycleId = () => {
    const existingIds = new Set([
      ...Object.keys(shopConfig.ShopItems || {}),
      ...Object.keys(shopConfig.SellItems || {}),
      ...Object.keys(shopConfig.Kits || {})
    ]);

    // 生成ID的策略：HS001, HS002, HS003...
    for (let i = 1; i <= 999; i++) {
      const id = `HS${i.toString().padStart(3, '0')}`;
      if (!existingIds.has(id)) {
        return id;
      }
    }
    
    // 如果HS系列用完了，使用时间戳
    return `RECYCLE_${Date.now()}`;
  };

  // 生成唯一的礼包ID
  const generateUniqueKitId = () => {
    const existingIds = new Set([
      ...Object.keys(shopConfig.ShopItems || {}),
      ...Object.keys(shopConfig.SellItems || {}),
      ...Object.keys(shopConfig.Kits || {})
    ]);

    // 生成ID的策略：K001, K002, K003...
    for (let i = 1; i <= 999; i++) {
      const id = `K${i.toString().padStart(3, '0')}`;
      if (!existingIds.has(id)) {
        return id;
      }
    }
    
    // 如果K系列用完了，使用时间戳
    return `KIT_${Date.now()}`;
  };

  // 添加物品到回收列表
  const addItemToRecycle = async (values: any) => {
    try {
      let itemId;
      
      if (autoGenerateRecycleId) {
        // 自动生成唯一的回收物品ID
        itemId = generateUniqueRecycleId();
      } else {
        // 使用手动输入的回收物品ID
        itemId = values.itemId;
        if (!itemId || !itemId.trim()) {
          message.error('请输入回收物品ID');
          return;
        }
        
        // 检查ID是否已存在
        const existingIds = new Set([
          ...Object.keys(shopConfig.ShopItems || {}),
          ...Object.keys(shopConfig.SellItems || {}),
          ...Object.keys(shopConfig.Kits || {})
        ]);
        
        if (existingIds.has(itemId)) {
          message.error(`回收物品ID "${itemId}" 已存在，请使用其他ID`);
          return;
        }
      }

      const newItem: any = {
        Amount: values.amount || 1,
        Blueprint: values.blueprint || '',
        Description: values.description || '',
        Price: values.price || 0,
        Type: values.itemType || 'item'
      };

      const newConfig = { ...(shopConfig || {}) } as any;
      if (!newConfig.SellItems) newConfig.SellItems = {};
      newConfig.SellItems[itemId] = newItem;

      await updateShopConfig(newConfig);
      message.success(`回收物品 ${itemId} 已添加${autoGenerateRecycleId ? '（ID自动生成）' : ''}`);
      recycleForm.resetFields();
      setRecycleModalVisible(false);
    } catch (e) {
      console.error(e);
      message.error('添加回收物品失败');
    }
  };


  // 玩家充值
  const handleRecharge = async (values: any) => {
    setRechargeLoading(true);
    try {
      const response = await api.post(`/shop/${serverId}/players/${values.steamId}/recharge`, {
        amount: values.amount,
        reason: values.reason
      });
      
      // 显示成功消息，包含更多信息
      const result = response.data;
      if (result.message) {
        message.success(result.message);
      } else {
        message.success('玩家充值成功');
      }
      
      setRechargeModalVisible(false);
      rechargeForm.resetFields();
    } catch (error: any) {
      console.error('玩家充值失败:', error);
      const errorMessage = error.response?.data?.message || '玩家充值失败';
      message.error(errorMessage);
    } finally {
      setRechargeLoading(false);
    }
  };

  useEffect(() => {
    checkShopPlugin();
  }, [serverId, checkShopPlugin]);

  useEffect(() => {
    if (isInstalled) {
      fetchShopConfig();
      fetchVipList();
    }
  }, [isInstalled, serverId, fetchShopConfig, fetchVipList]);


  // 在售商品列定义（显示商店配置中的商品）
  const sellItemColumns = [
    {
      title: '商品ID',
      dataIndex: 'id',
      key: 'id',
      width: 100
    },
    {
      title: '商品名称',
      dataIndex: 'Description',
      key: 'Description',
      width: 200
    },
    {
      title: '价格',
      dataIndex: 'Price',
      key: 'Price',
      width: 100,
      render: (price: number) => `${price} 金币`
    },
    {
      title: '商品类型',
      dataIndex: 'Type',
      key: 'Type',
      width: 80,
      render: (type: string) => {
        const typeMap: { [key: string]: string } = {
          'item': '物品',
          'dino': '恐龙',
          'experience': '经验值',
          'beacon': '光柱宝箱',
          'command': '控制台命令',
          'engram': '印痕解锁'
        };
        const chineseType = typeMap[type] || type;
        return <Tag color="green">{chineseType}</Tag>;
      }
    },
    {
      title: '来源',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => <Tag color={type === 'SellItems' ? 'blue' : 'orange'}>{type}</Tag>
    },
    {
      title: '权限',
      dataIndex: 'Permissions',
      key: 'Permissions',
      width: 150,
      render: (permissions: any) => (
        <div>
          {permissions && typeof permissions === 'string' && permissions.split(',').map((perm: string, index: number) => (
            <Tag key={index} color="blue">{perm}</Tag>
          ))}
          {permissions && Array.isArray(permissions) && permissions.map((perm: string, index: number) => (
            <Tag key={index} color="blue">{perm}</Tag>
          ))}
        </div>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (text: any, record: any) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedItem(record);
              // 设置表单初始值，只设置原有字段
              const initialValues: any = {};
              Object.keys(record).forEach(key => {
                if (key !== 'type' && key !== 'id') {
                  if (key === 'Permissions' && record[key]) {
                    if (typeof record[key] === 'string') {
                      initialValues[key] = record[key].split(',');
                    } else if (Array.isArray(record[key])) {
                      initialValues[key] = record[key];
                    } else {
                      initialValues[key] = [];
                    }
                  } else if (key === 'Items' && Array.isArray(record[key])) {
                    // Items数组直接使用，不需要JSON字符串化
                    initialValues[key] = record[key];
                  } else if (Array.isArray(record[key]) || (typeof record[key] === 'object' && record[key] !== null)) {
                    // 对于其他数组和对象类型，转换为JSON字符串显示
                    initialValues[key] = JSON.stringify(record[key], null, 2);
                  } else {
                    initialValues[key] = record[key];
                  }
                }
              });
              editItemForm.setFieldsValue(initialValues);
              
              // 初始化Items数组的独立状态
              if (record.Items && Array.isArray(record.Items)) {
                setItemsArrayState([...record.Items]);
              } else {
                setItemsArrayState([]);
              }
              
              setEditItemModalVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个商品吗？"
            onConfirm={async () => {
              try {
                await api.delete(`/shop/${serverId}/items/${record.id}`);
                message.success('删除成功');
                // 刷新商店配置
                await fetchShopConfig();
              } catch (error) {
                console.error('删除商品失败:', error);
                message.error('删除商品失败');
              }
            }}
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];



  // 购买记录列定义
  const recordColumns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 120
    },
    {
      title: '商品',
      dataIndex: 'item',
      key: 'item',
      ellipsis: true
    }
  ];

  if (!isInstalled) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Alert
          message="商店插件未安装"
          description="请先在插件管理中安装商店数据包插件，然后才能使用商店管理功能。"
          type="warning"
          showIcon
        />
        <div style={{ marginTop: '24px' }}>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => {
              checkShopPlugin();
              message.info('正在重新检查插件状态...');
            }}
            loading={loading}
          >
            重新检查插件状态
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShoppingCartOutlined />
            <span>商店管理</span>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchShopConfig();
                fetchPurchaseRecords(1);
              }}
            >
              刷新
            </Button>
          </div>
        }
        style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Tabs
          defaultActiveKey="sell-items"
          type="card"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '8px',
            padding: '16px'
          }}
        >
          <TabPane
            tab={
              <span>
                <ShoppingCartOutlined />
                在售商品
              </span>
            }
            key="sell-items"
          >
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddItemModalVisible(true)}
                >
                  添加商品
                </Button>
                <Button
                  type="default"
                  icon={<DollarOutlined />}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                  onClick={() => setBatchPriceModalVisible(true)}
                >
                  批量设置价格
                </Button>
                <Popconfirm
                  title="确定要清空商品分类下的所有商品吗？"
                  description="此操作将删除商店物品中的所有商品，但不会影响回收的商品。此操作不可撤销！"
                  onConfirm={clearShopItems}
                  okText="确定清空"
                  cancelText="取消"
                  okType="danger"
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                  >
                    清空商品
                  </Button>
                </Popconfirm>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    fetchShopConfig();
                    message.success('刷新成功');
                  }}
                >
                  刷新
                </Button>
              </Space>
            </div>
            
            <Table
              columns={sellItemColumns}
              dataSource={[
                // SellItems 商品
                ...Object.entries(shopConfig.SellItems || {}).map(([key, value]: [string, any]) => ({
                  id: key,
                  type: 'SellItems',
                  ...value
                })),
                // ShopItems 商品
                ...Object.entries(shopConfig.ShopItems || {}).map(([key, value]: [string, any]) => ({
                  id: key,
                  type: 'ShopItems',
                  ...value
                }))
              ]}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize: 20,
                total: (Object.keys(shopConfig.SellItems || {}).length + Object.keys(shopConfig.ShopItems || {}).length),
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                },
                onShowSizeChange: (current, size) => {
                  setCurrentPage(1);
                }
              }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <GiftOutlined />
                礼包设置
              </span>
            }
            key="kits"
          >
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddKitModalVisible(true)}
                >
                  添加礼包
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    fetchShopConfig();
                    message.success('刷新成功');
                  }}
                >
                  刷新
                </Button>
              </Space>
            </div>
            
            <Table
              columns={sellItemColumns}
              dataSource={[
                // Kits 礼包
                ...Object.entries(shopConfig.Kits || {}).map(([key, value]: [string, any]) => ({
                  id: key,
                  type: 'Kits',
                  ...value
                }))
              ]}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize: 20,
                total: Object.keys(shopConfig.Kits || {}).length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                },
                onShowSizeChange: (current, size) => {
                  setCurrentPage(1);
                }
              }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <ReloadOutlined />
                物品回收
              </span>
            }
            key="recycle"
          >
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setRecycleModalVisible(true)}
                >
                  添加回收物品
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    fetchShopConfig();
                    message.success('刷新成功');
                  }}
                >
                  刷新
                </Button>
              </Space>
            </div>
            
            <Alert
              message="物品回收功能"
              description="此功能允许玩家将物品出售给商店获得金币。添加的物品将出现在 SellItems 分类中。"
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            
            <Table
              columns={sellItemColumns}
              dataSource={[
                // SellItems 商品
                ...Object.entries(shopConfig.SellItems || {}).map(([key, value]: [string, any]) => ({
                  id: key,
                  type: 'SellItems',
                  ...value
                }))
              ]}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize: 20,
                total: Object.keys(shopConfig.SellItems || {}).length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                },
                onShowSizeChange: (current, size) => {
                  setCurrentPage(1);
                }
              }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <CrownOutlined />
                VIP设置
              </span>
            }
            key="vip-settings"
          >
            <Card title="在线金币奖励配置" size="small" style={{ marginBottom: 16 }}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="参数说明"
                description={
                  <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                    <div><strong>启用奖励</strong>：是否开启在线金币奖励功能</div>
                    <div><strong>奖励周期</strong>：玩家每隔多少分钟获得一次奖励（单位：分钟）</div>
                    <div><strong>奖励叠加</strong>：是否允许奖励叠加累积，开启后玩家离线时奖励会累积</div>
                    <div><strong>用户组</strong>：为不同用户组设置不同的奖励金额</div>
                  </div>
                }
              />

              <Form layout="vertical">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="启用在线金币奖励">
                      <Switch
                        checked={timedPointsEnabled}
                        onChange={setTimedPointsEnabled}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="奖励周期（分钟）">
                      <InputNumber
                        min={1}
                        max={1440}
                        value={rewardInterval}
                        onChange={(value) => setRewardInterval(value || 1)}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="奖励叠加">
                  <Switch
                    checked={stackRewards}
                    onChange={setStackRewards}
                  />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    开启后，玩家离线时奖励会累积，上线时一次性发放
                  </div>
                </Form.Item>

                <Divider>用户组奖励设置</Divider>

                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="dashed"
                    onClick={handleAddVipGroup}
                    style={{ width: '100%' }}
                    icon={<PlusOutlined />}
                  >
                    添加用户组
                  </Button>
                </div>

                {vipGroups.map((group, index) => (
                  <Card
                    key={index}
                    size="small"
                    style={{ marginBottom: 8 }}
                    title={`用户组 ${index + 1}`}
                    extra={
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveVipGroup(index)}
                      >
                        删除
                      </Button>
                    }
                  >
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item label="用户组名称" style={{ marginBottom: 0 }}>
                          <Input
                            placeholder="如：Default, VIP1, Premiums"
                            value={group.name}
                            onChange={(e) => handleVipGroupChange(index, 'name', e.target.value)}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="奖励金额" style={{ marginBottom: 0 }}>
                          <InputNumber
                            min={0}
                            value={group.amount}
                            onChange={(value) => handleVipGroupChange(index, 'amount', value || 0)}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}

                {vipGroups.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                    暂无用户组，点击上方按钮添加
                  </div>
                )}

                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Button
                    type="primary"
                    onClick={handleSaveVipSettings}
                    loading={loading}
                    icon={<SettingOutlined />}
                  >
                    保存设置
                  </Button>
                </div>
              </Form>
            </Card>
          </TabPane>

          <TabPane
            tab={
              <span>
                <BellOutlined />
                消息提示
              </span>
            }
            key="messages"
          >
            <div style={{ marginBottom: '16px' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    fetchShopConfig();
                    message.success('刷新成功');
                  }}
                >
                  刷新
                </Button>
                <Button
                  type="default"
                  icon={<SettingOutlined />}
                  onClick={() => {
                    // 保存消息配置
                    handleSaveMessages();
                  }}
                  loading={loading}
                >
                  保存消息配置
                </Button>
              </Space>
            </div>
            
            <Alert
              message="消息提示配置"
              description="这里可以配置商店插件在游戏中的各种提示消息。支持 RichColor 标签来设置颜色和格式。"
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            
            <Collapse
              defaultActiveKey={['commands', 'success', 'errors']}
              items={[
                {
                  key: 'commands',
                  label: '命令配置',
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Form.Item label="购买命令">
                          <Input
                            value={shopConfig.Messages?.BuyCmd || ''}
                            onChange={(e) => updateMessageConfig('BuyCmd', e.target.value)}
                            placeholder="/buy"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="礼包购买命令">
                          <Input
                            value={shopConfig.Messages?.BuyKitCmd || ''}
                            onChange={(e) => updateMessageConfig('BuyKitCmd', e.target.value)}
                            placeholder="/buykit"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="出售命令">
                          <Input
                            value={shopConfig.Messages?.SellCmd || ''}
                            onChange={(e) => updateMessageConfig('SellCmd', e.target.value)}
                            placeholder="/sell"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="商店命令">
                          <Input
                            value={shopConfig.Messages?.ShopCmd || ''}
                            onChange={(e) => updateMessageConfig('ShopCmd', e.target.value)}
                            placeholder="/shop"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="礼包命令">
                          <Input
                            value={shopConfig.Messages?.KitCmd || ''}
                            onChange={(e) => updateMessageConfig('KitCmd', e.target.value)}
                            placeholder="/kit"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="金币命令">
                          <Input
                            value={shopConfig.Messages?.PointsCmd || ''}
                            onChange={(e) => updateMessageConfig('PointsCmd', e.target.value)}
                            placeholder="/points"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="转账命令">
                          <Input
                            value={shopConfig.Messages?.TradeCmd || ''}
                            onChange={(e) => updateMessageConfig('TradeCmd', e.target.value)}
                            placeholder="/trade"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="帮助命令">
                          <Input
                            value={shopConfig.Messages?.HelpCmd || ''}
                            onChange={(e) => updateMessageConfig('HelpCmd', e.target.value)}
                            placeholder="/shophelp"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="商店查找命令">
                          <Input
                            value={shopConfig.Messages?.ShopFindCmd || ''}
                            onChange={(e) => updateMessageConfig('ShopFindCmd', e.target.value)}
                            placeholder="/shopfind"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="商店出售命令">
                          <Input
                            value={shopConfig.Messages?.ShopSellCmd || ''}
                            onChange={(e) => updateMessageConfig('ShopSellCmd', e.target.value)}
                            placeholder="/shopsell"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                },
                {
                  key: 'success',
                  label: '成功提示消息',
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.BoughtItem || ''}
                          onChange={(value) => updateMessageConfig('BoughtItem', value)}
                          placeholder="恭喜你购买成功！"
                          label="购买成功提示"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.BoughtKit || ''}
                          onChange={(value) => updateMessageConfig('BoughtKit', value)}
                          placeholder="您已经成功地购买了 {0} 礼包!"
                          label="礼包购买成功"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.BoughtBeacon || ''}
                          onChange={(value) => updateMessageConfig('BoughtBeacon', value)}
                          placeholder="恭喜你购买成功！"
                          label="光柱购买成功"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.BoughtDino || ''}
                          onChange={(value) => updateMessageConfig('BoughtDino', value)}
                          placeholder="恭喜你购买成功！"
                          label="恐龙购买成功"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.BoughtExp || ''}
                          onChange={(value) => updateMessageConfig('BoughtExp', value)}
                          placeholder="恭喜你购买成功！"
                          label="经验购买成功"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.SoldItems || ''}
                          onChange={(value) => updateMessageConfig('SoldItems', value)}
                          placeholder="出售成功！"
                          label="出售成功"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.ReceivedPoints || ''}
                          onChange={(value) => updateMessageConfig('ReceivedPoints', value)}
                          placeholder="你收到 {0} 金币! (总计: {1})"
                          label="收到金币"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.GotPoints || ''}
                          onChange={(value) => updateMessageConfig('GotPoints', value)}
                          placeholder="你收到金币 {0} ，来自于 {1}"
                          label="获得金币"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.SentPoints || ''}
                          onChange={(value) => updateMessageConfig('SentPoints', value)}
                          placeholder="您转账 {0} 金币发送到 {1}"
                          label="转账成功"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.HavePoints || ''}
                          onChange={(value) => updateMessageConfig('HavePoints', value)}
                          placeholder="你拥有 {0} 金币"
                          label="当前金币"
                        />
                      </Col>
                    </Row>
                  )
                },
                {
                  key: 'errors',
                  label: '错误提示消息',
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.NoPoints || ''}
                          onChange={(value) => updateMessageConfig('NoPoints', value)}
                          placeholder="你没有足够的点券或金币请联系群主："
                          label="金币不足"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.InventoryIsFull || ''}
                          onChange={(value) => updateMessageConfig('InventoryIsFull', value)}
                          placeholder="背包已满!"
                          label="背包已满"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.NoPermissionsStore || ''}
                          onChange={(value) => updateMessageConfig('NoPermissionsStore', value)}
                          placeholder="你无购买此礼包的权限{0}!"
                          label="权限不足"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.NoPermissionsKit || ''}
                          onChange={(value) => updateMessageConfig('NoPermissionsKit', value)}
                          placeholder="您的代码输入错误/kit A1 分大小写, 请联系服主开通会员享受在线金币加成!"
                          label="礼包权限不足"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.CantBuyKit || ''}
                          onChange={(value) => updateMessageConfig('CantBuyKit', value)}
                          placeholder="你不能购买这个礼包!"
                          label="无法购买礼包"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.NoPlayer || ''}
                          onChange={(value) => updateMessageConfig('NoPlayer', value)}
                          placeholder="玩家不存在!"
                          label="玩家不存在"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.NotEnoughItems || ''}
                          onChange={(value) => updateMessageConfig('NotEnoughItems', value)}
                          placeholder="你没有足够的物品出售！({0}/{1})"
                          label="物品不足"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.RidingDino || ''}
                          onChange={(value) => updateMessageConfig('RidingDino', value)}
                          placeholder="骑乘时不能买这个物品!"
                          label="骑乘限制"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.WrongId || ''}
                          onChange={(value) => updateMessageConfig('WrongId', value)}
                          placeholder="ID错误"
                          label="ID错误"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.RefundError || ''}
                          onChange={(value) => updateMessageConfig('RefundError', value)}
                          placeholder="出现系统错误"
                          label="系统错误"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.BadLevel || ''}
                          onChange={(value) => updateMessageConfig('BadLevel', value)}
                          placeholder="要求等级范围: {0} - {1}"
                          label="等级要求提示"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.FoundMorePlayers || ''}
                          onChange={(value) => updateMessageConfig('FoundMorePlayers', value)}
                          placeholder="找到了多个具有指定名称的玩家!"
                          label="找到多个玩家"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.CantGivePoints || ''}
                          onChange={(value) => updateMessageConfig('CantGivePoints', value)}
                          placeholder="你不能给自己金币!"
                          label="不能给自己金币"
                        />
                      </Col>
                    </Row>
                  )
                },
                {
                  key: 'kits',
                  label: '礼包相关消息',
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.AvailableKits || ''}
                          onChange={(value) => updateMessageConfig('AvailableKits', value)}
                          placeholder="Available kits for you:"
                          label="可用礼包提示"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.NoKits || ''}
                          onChange={(value) => updateMessageConfig('NoKits', value)}
                          placeholder="没有可用的礼包!"
                          label="无可用礼包"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.NoKitsLeft || ''}
                          onChange={(value) => updateMessageConfig('NoKitsLeft', value)}
                          placeholder="您没有剩余{0}个礼包!"
                          label="礼包次数用完"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.KitsLeft || ''}
                          onChange={(value) => updateMessageConfig('KitsLeft', value)}
                          placeholder="你剩下{0} {1}个礼包!"
                          label="礼包剩余次数"
                        />
                      </Col>
                      <Col span={12}>
                        <ColorMessageInput
                          value={shopConfig.Messages?.OnlyOnSpawnKit || ''}
                          onChange={(value) => updateMessageConfig('OnlyOnSpawnKit', value)}
                          placeholder="此礼包仅出生可领"
                          label="礼包仅出生可领"
                        />
                      </Col>
                      <Col span={12}>
                        <Form.Item label="礼包使用说明">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.KitUsage || ''}
                            onChange={(e) => updateMessageConfig('KitUsage', e.target.value)}
                            placeholder="领取命令：/kit ID"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="礼包列表格式">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.KitsListFormat || ''}
                            onChange={(e) => updateMessageConfig('KitsListFormat', e.target.value)}
                            placeholder="{1}，礼包ID：{0}  免费领取次数：:{2}个   {3}\n"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="礼包价格显示">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.KitsListPrice || ''}
                            onChange={(e) => updateMessageConfig('KitsListPrice', e.target.value)}
                            placeholder="购买价格:{0}金币"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                },
                {
                  key: 'usage',
                  label: '使用说明消息',
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Form.Item label="购买说明">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.BuyUsage || ''}
                            onChange={(e) => updateMessageConfig('BuyUsage', e.target.value)}
                            placeholder="购买说明:/buy id"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="礼包购买说明">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.BuyKitUsage || ''}
                            onChange={(e) => updateMessageConfig('BuyKitUsage', e.target.value)}
                            placeholder="购买礼包:/buykit 礼包ID 数量"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="出售说明">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.SellUsage || ''}
                            onChange={(e) => updateMessageConfig('SellUsage', e.target.value)}
                            placeholder="出售说明:/sell ID 数量"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="转账说明">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.TradeUsage || ''}
                            onChange={(e) => updateMessageConfig('TradeUsage', e.target.value)}
                            placeholder="转账指令：/trade 玩家名字 数量"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="商店使用说明">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.ShopUsage || ''}
                            onChange={(e) => updateMessageConfig('ShopUsage', e.target.value)}
                            placeholder="商店指令: /shop 页数"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="商店查找使用说明">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.ShopFindUsage || ''}
                            onChange={(e) => updateMessageConfig('ShopFindUsage', e.target.value)}
                            placeholder="使用方法：/shopfind 关键词"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="商店帮助信息">
                          <Input.TextArea
                            rows={3}
                            value={shopConfig.Messages?.HelpMessage || ''}
                            onChange={(e) => updateMessageConfig('HelpMessage', e.target.value)}
                            placeholder="这是商店帮助信息"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="商店消息">
                          <Input.TextArea
                            rows={3}
                            value={shopConfig.Messages?.ShopMessage || ''}
                            onChange={(e) => updateMessageConfig('ShopMessage', e.target.value)}
                            placeholder="购买指令：/buy ID 数量词"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                },
                {
                  key: 'search',
                  label: '查找功能消息',
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Form.Item label="商店查找未找到">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.ShopFindNotFound || ''}
                            onChange={(e) => updateMessageConfig('ShopFindNotFound', e.target.value)}
                            placeholder="未找到指定商品"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="商店查找结果过多">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.ShopFindTooManyResults || ''}
                            onChange={(e) => updateMessageConfig('ShopFindTooManyResults', e.target.value)}
                            placeholder="找到物品过多"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                },
                {
                  key: 'display',
                  label: '显示格式配置',
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Form.Item label="商店列表格式">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.StoreListFormat || ''}
                            onChange={(e) => updateMessageConfig('StoreListFormat', e.target.value)}
                            placeholder="{0}"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="商店物品格式">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.StoreListItem || ''}
                            onChange={(e) => updateMessageConfig('StoreListItem', e.target.value)}
                            placeholder="{0:5}、\t\t\t{2:10}\t\t\t{1:15}\t\t\t{3:10}金币\n"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="商店恐龙格式">
                          <Input.TextArea
                            rows={2}
                            value={shopConfig.Messages?.StoreListDino || ''}
                            onChange={(e) => updateMessageConfig('StoreListDino', e.target.value)}
                            placeholder="{0:5}、\t\t\t{3:15}\t\t\t{1}\t\t\t{2:10}级\t\t\t{4:10}金币\n"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="发送者名称">
                          <Input
                            value={shopConfig.Messages?.Sender || ''}
                            onChange={(e) => updateMessageConfig('Sender', e.target.value)}
                            placeholder="系统商店"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                }
              ]}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <DollarOutlined />
                玩家充值
              </span>
            }
            key="recharge"
          >
            <div style={{ marginBottom: '16px' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setRechargeModalVisible(true)}
              >
                玩家充值
              </Button>
            </div>
            
            <Alert
              message="玩家充值功能"
              description="此功能将充值金额添加到ArkShop数据库中的Players表，直接增加玩家的Points余额。"
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <HistoryOutlined />
                购买记录
              </span>
            }
            key="records"
          >
            <Table
              columns={recordColumns}
              dataSource={purchaseRecords}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 添加商品模态框 */}
      <Modal
        title="添加商品到商店"
        open={addItemModalVisible}
        onCancel={() => {
          setAddItemModalVisible(false);
          addItemForm.resetFields();
          setItemsArrayState([]);
          setDinosArrayState([]);
        }}
        onOk={() => addItemForm.submit()}
        width={1000}
      >
        <Card
          title="新增商品"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="参数说明"
            description={
              <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                <div><strong>商品ID</strong>：可选择自动生成（如 A001、A002...）或手动输入自定义ID。</div>
                <div><strong>商品名称</strong>：商品在游戏中显示的名称。</div>
                <div><strong>商品类型</strong>：物品、恐龙、武器、护甲等不同类型。</div>
                <div><strong>蓝图路径</strong>：物品在游戏中的蓝图路径，可通过选择器选择。</div>
                <div><strong>价格</strong>：商品的金币价格。</div>
                <div><strong>权限</strong>：限制可购买的用户组（逗号分隔），留空代表不限。</div>
                <div><strong>等级限制</strong>：MinLevel / MaxLevel；不填则默认 1 / 9999。</div>
                <div><strong>物品属性</strong>：包含数量、品质、耐久、护甲、伤害等属性。</div>
                <div><strong>恐龙属性</strong>：包含等级、性别、绝育、鞍具蓝图等属性。</div>
              </div>
            }
          />

          <Form
            form={addItemForm}
            layout="vertical"
            onFinish={addItemToShop}
          >
            <Form.Item label="商品ID生成方式">
              <Switch
                checked={autoGenerateItemId}
                onChange={setAutoGenerateItemId}
                checkedChildren="自动生成"
                unCheckedChildren="手动输入"
              />
            </Form.Item>

            {!autoGenerateItemId && (
              <Form.Item name="itemId" label="商品ID" rules={[{ required: true, message: '请输入商品ID' }]}>
                <Input placeholder="如 A1 / C001 / D001" />
              </Form.Item>
            )}

            <Form.Item name="description" label="商品名称" rules={[{ required: true, message: '请输入商品名称' }]}>
              <Input placeholder="商品在游戏中显示的名称" />
            </Form.Item>

            <Form.Item name="itemType" label="商品类型" rules={[{ required: true, message: '请选择商品类型' }]}>
              <Select placeholder="选择商品类型">
                <Option value="item">物品</Option>
                <Option value="dino">恐龙</Option>
                <Option value="armor">护甲</Option>
                <Option value="weapon">武器</Option>
                <Option value="tool">工具</Option>
                <Option value="structure">建筑</Option>
                <Option value="resource">资源</Option>
                <Option value="food">食物</Option>
                <Option value="mod">MOD物品</Option>
                <Option value="experience">经验值</Option>
                <Option value="beacon">光柱宝箱</Option>
                <Option value="command">控制台命令</Option>
                <Option value="engram">印痕解锁</Option>
              </Select>
            </Form.Item>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="price" label="价格(金币)" rules={[{ required: true, message: '请输入商品价格' }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="permissions" label="权限(可多选)">
                  <Select mode="multiple" placeholder="不选表示不限用户组">
                    {vipList
                      .filter((v: string) => v && v.trim())
                      .map((v: string) => (
                        <Option key={v} value={v}>{v}</Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="minLevel" label="最低等级" initialValue={1}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxLevel" label="最高等级" initialValue={9999}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider>商品内容 - 物品</Divider>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              {/* 表头 */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1.5fr 1.5fr',
                  gap: 8,
                  fontWeight: 600,
                  padding: '8px 4px',
                  color: '#555'
                }}
              >
                <div>蓝图路径</div>
                <div>数量</div>
                <div>品质</div>
                <div>耐久</div>
                <div>护甲</div>
                <div>伤害</div>
                <div>强制蓝图</div>
                <div>固定属性</div>
              </div>

              <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="dashed"
                onClick={() => setItemsArrayState([...itemsArrayState, {
                  Amount: 1,
                  Blueprint: '',
                  Quality: 0,
                  ForceBlueprint: false,
                  Fixed: false,
                  Durability: 0,
                  Armor: 0,
                  Damage: 0
                }])}
              >
                + 添加物品条目
              </Button>
                {itemsArrayState.map((it, idx) => (
                  <Card key={`item-${idx}`} size="small" title={`物品 ${idx + 1}`} style={{ marginBottom: 8 }}
                    extra={
                      <Button type="text" danger size="small" onClick={() => {
                        const arr = itemsArrayState.filter((_, i) => i !== idx);
                        setItemsArrayState(arr);
                      }}>删除</Button>
                    }
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1.5fr 1.5fr',
                        gap: 8,
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <ARKItemSelector
                          value={it.Blueprint}
                          placeholder="蓝图路径(Blueprint)"
                          onChange={(value) => {
                            const arr = [...itemsArrayState];
                            arr[idx] = { ...arr[idx], Blueprint: value };
                            setItemsArrayState(arr);
                          }}
                        />
                      </div>
                      <InputNumber min={1} placeholder="数量" value={it.Amount}
                        onChange={(v) => {
                          const arr = [...itemsArrayState];
                          arr[idx] = { ...arr[idx], Amount: v };
                          setItemsArrayState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber min={0} max={100} placeholder="品质"
                        value={it.Quality}
                        onChange={(v) => {
                          const arr = [...itemsArrayState];
                          arr[idx] = { ...arr[idx], Quality: v };
                          setItemsArrayState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber min={0} placeholder="耐久" value={it.Durability}
                        onChange={(v) => {
                          const arr = [...itemsArrayState];
                          arr[idx] = { ...arr[idx], Durability: v };
                          setItemsArrayState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber min={0} placeholder="护甲" value={it.Armor}
                        onChange={(v) => {
                          const arr = [...itemsArrayState];
                          arr[idx] = { ...arr[idx], Armor: v };
                          setItemsArrayState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber min={0} placeholder="伤害" value={it.Damage}
                        onChange={(v) => {
                          const arr = [...itemsArrayState];
                          arr[idx] = { ...arr[idx], Damage: v };
                          setItemsArrayState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <div>
                        <Switch
                          checked={!!it.ForceBlueprint}
                          onChange={(v) => {
                            const arr = [...itemsArrayState];
                            arr[idx] = { ...arr[idx], ForceBlueprint: v };
                            setItemsArrayState(arr);
                          }}
                        /> 强制蓝图
                      </div>
                      <div>
                        <Switch
                          checked={!!it.Fixed}
                          onChange={(v) => {
                            const arr = [...itemsArrayState];
                            arr[idx] = { ...arr[idx], Fixed: v };
                            setItemsArrayState(arr);
                          }}
                        /> 固定属性
                      </div>
                    </div>
                  </Card>
                ))}
              </Space>
            </div>

            <Divider>商品内容 - 恐龙</Divider>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              {/* 表头 */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr 1fr',
                  gap: 8,
                  fontWeight: 600,
                  padding: '8px 4px',
                  color: '#555'
                }}
              >
                <div>蓝图路径</div>
                <div>等级</div>
                <div>性别</div>
                <div>鞍具蓝图</div>
                <div>绝育</div>
                <div>禁止低温</div>
              </div>

              <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="dashed"
                onClick={() => setDinosArrayState([...dinosArrayState, {
                  Blueprint: '',
                  Level: 1,
                  Gender: 'random',
                  Neutered: false,
                  PreventCryo: false,
                  SaddleBlueprint: ''
                }])}
              >
                + 添加恐龙条目
              </Button>
                {dinosArrayState.map((dn, idx) => (
                  <Card key={`dino-${idx}`} size="small" title={`恐龙 ${idx + 1}`} style={{ marginBottom: 8 }}
                    extra={
                      <Button type="text" danger size="small" onClick={() => {
                        const arr = dinosArrayState.filter((_, i) => i !== idx);
                        setDinosArrayState(arr);
                      }}>删除</Button>
                    }
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr 1fr',
                        gap: 8,
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <ARKItemSelector
                          value={dn.Blueprint}
                          placeholder="蓝图路径(Blueprint)"
                          onChange={(value) => {
                            const arr = [...dinosArrayState];
                            arr[idx] = { ...arr[idx], Blueprint: value };
                            setDinosArrayState(arr);
                          }}
                        />
                      </div>
                      <InputNumber min={1} placeholder="等级(Level)" value={dn.Level}
                        onChange={(v) => {
                          const arr = [...dinosArrayState];
                          arr[idx] = { ...arr[idx], Level: v };
                          setDinosArrayState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <Select
                        value={dn.Gender}
                        onChange={(v) => {
                          const arr = [...dinosArrayState];
                          arr[idx] = { ...arr[idx], Gender: v };
                          setDinosArrayState(arr);
                        }}
                        style={{ width: '100%' }}
                      >
                        <Option value="random">随机</Option>
                        <Option value="male">雄性</Option>
                        <Option value="female">雌性</Option>
                      </Select>
                      <div>
                        <ARKItemSelector
                          value={dn.SaddleBlueprint}
                          placeholder="鞍具蓝图(SaddleBlueprint，可选)"
                          onChange={(value) => {
                            const arr = [...dinosArrayState];
                            arr[idx] = { ...arr[idx], SaddleBlueprint: value };
                            setDinosArrayState(arr);
                          }}
                        />
                      </div>
                      <div>
                        <Switch
                          checked={!!dn.Neutered}
                          onChange={(v) => {
                            const arr = [...dinosArrayState];
                            arr[idx] = { ...arr[idx], Neutered: v };
                            setDinosArrayState(arr);
                          }}
                        /> 绝育
                      </div>
                      <div>
                        <Switch
                          checked={!!dn.PreventCryo}
                          onChange={(v) => {
                            const arr = [...dinosArrayState];
                            arr[idx] = { ...arr[idx], PreventCryo: v };
                            setDinosArrayState(arr);
                          }}
                        /> 禁止低温
                      </div>
                    </div>
                  </Card>
                ))}
              </Space>
            </div>

            <Divider />
            <Space>
              <Button type="primary" htmlType="submit">保存商品</Button>
              <Button
                onClick={() => {
                  addItemForm.resetFields();
                  setItemsArrayState([]);
                  setDinosArrayState([]);
                }}
              >
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Modal>

      {/* 玩家充值模态框 */}
      <Modal
        title="玩家充值"
        open={rechargeModalVisible}
        onCancel={() => {
          setRechargeModalVisible(false);
          rechargeForm.resetFields();
        }}
        onOk={() => rechargeForm.submit()}
        confirmLoading={rechargeLoading}
        okText="确认充值"
        cancelText="取消"
      >
        <Form
          form={rechargeForm}
          layout="vertical"
          onFinish={handleRecharge}
        >
          <Form.Item
            name="steamId"
            label="Steam ID"
            rules={[
              { required: true, message: '请输入Steam ID' },
              { pattern: /^\d{17}$/, message: 'Steam ID应为17位数字' }
            ]}
          >
            <Input placeholder="输入17位Steam ID" />
          </Form.Item>

          <Form.Item
            name="amount"
            label="充值金额"
            rules={[{ required: true, message: '请输入充值金额' }]}
          >
            <InputNumber
              min={1}
              placeholder="输入充值金额"
              style={{ width: '100%' }}
              addonAfter="金币"
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="充值原因"
          >
            <TextArea rows={3} placeholder="输入充值原因" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑商品模态框 */}
      <Modal
        title="编辑商品"
        open={editItemModalVisible}
        onCancel={() => {
          setEditItemModalVisible(false);
          setEditItemLoading(false);
        }}
        confirmLoading={editItemLoading}
        onOk={() => {
          // 防止重复点击
          if (editItemLoading) return;
          
          editItemForm.validateFields().then(async (values) => {
            setEditItemLoading(true);
            try {
              // 处理表单值，将JSON字符串解析回对象/数组
              const processedValues: any = {};
              Object.keys(values).forEach(key => {
                const value = values[key];
                if (key === 'Items') {
                  // 使用独立状态中的Items数组数据
                  processedValues[key] = itemsArrayState;
                } else if (key === 'Permissions') {
                  // 权限字段：如果是数组，转换为逗号分隔的字符串
                  if (Array.isArray(value)) {
                    processedValues[key] = value.join(',');
                  } else {
                    processedValues[key] = value;
                  }
                } else if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
                  try {
                    // 尝试解析JSON字符串
                    processedValues[key] = JSON.parse(value);
                  } catch (e) {
                    // 如果解析失败，保持原字符串
                    processedValues[key] = value;
                  }
                } else {
                  processedValues[key] = value;
                }
              });
              
              // 调用编辑商品API，不包含type字段
              await api.put(`/shop/${serverId}/items/${selectedItem?.id}`, processedValues);
              
              message.success('商品编辑成功');
              setEditItemModalVisible(false);
              editItemForm.resetFields();
              setItemsArrayState([]); // 清空独立状态
              
              // 刷新商店配置
              await fetchShopConfig();
            } catch (error) {
              console.error('编辑商品失败:', error);
              message.error('编辑商品失败');
            } finally {
              setEditItemLoading(false);
            }
          });
        }}
        width={600}
      >
        <Form form={editItemForm} layout="vertical">
          {selectedItem && Object.keys(selectedItem).map((key) => {
            if (key === 'type' || key === 'id') return null; // 跳过内部字段
            
            const value = (selectedItem as any)[key];
            const label = getFieldLabel(key);
            
            return (
              <Form.Item
                key={key}
                name={key}
                label={label}
                rules={key === 'Description' || key === 'Price' ? [{ required: true, message: `请输入${label}` }] : []}
                valuePropName={key === 'blueprint' ? 'checked' : undefined}
              >
                {renderFormField(key, value)}
              </Form.Item>
            );
          })}
        </Form>
      </Modal>

      {/* 批量设置价格模态框 */}
      <Modal
        title="批量设置价格"
        open={batchPriceModalVisible}
        onCancel={() => {
          setBatchPriceModalVisible(false);
          batchPriceForm.resetFields();
        }}
        onOk={() => batchPriceForm.submit()}
        confirmLoading={batchPriceLoading}
        okText="确定设置"
        cancelText="取消"
        width={500}
      >
        <Form
          form={batchPriceForm}
          layout="vertical"
          onFinish={handleBatchPriceSubmit}
        >
          <Alert
            message="批量设置价格说明"
            description={
              <div>
                <p>此操作将把ShopItems分类下所有商品的价格设置为指定值。</p>
                <p>当前ShopItems分类下有 <strong>{Object.keys(shopConfig.ShopItems || {}).length}</strong> 个商品。</p>
                <p style={{ color: '#ff4d4f', marginTop: 8 }}>
                  <strong>注意：此操作不可撤销！</strong>
                </p>
              </div>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 24 }}
          />
          
          <Form.Item
            name="price"
            label="设置价格"
            rules={[
              { required: true, message: '请输入价格' },
              { type: 'number', min: 0, message: '价格不能为负数' }
            ]}
          >
            <InputNumber
              min={0}
              placeholder="输入要设置的价格"
              style={{ width: '100%' }}
              addonAfter="金币"
              size="large"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加回收物品模态框 */}
      <Modal
        title="添加回收物品"
        open={recycleModalVisible}
        onCancel={() => {
          setRecycleModalVisible(false);
          recycleForm.resetFields();
        }}
        onOk={() => recycleForm.submit()}
        width={600}
      >
        <Card
          title="新增回收物品"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="参数说明"
            description={
              <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                <div><strong>回收ID</strong>：可选择自动生成（如 HS001、HS002...）或手动输入自定义ID。</div>
                <div><strong>物品名称</strong>：物品在游戏中显示的名称。</div>
                <div><strong>蓝图路径</strong>：物品在游戏中的蓝图路径，可通过选择器选择。</div>
                <div><strong>回收价格</strong>：玩家出售该物品时获得的金币数量。</div>
                <div><strong>数量</strong>：每次回收的物品数量。</div>
                <div><strong>物品类型</strong>：物品类型，通常为 "item"。</div>
              </div>
            }
          />

          <Form
            form={recycleForm}
            layout="vertical"
            onFinish={addItemToRecycle}
          >
            <Form.Item label="回收物品ID生成方式">
              <Switch
                checked={autoGenerateRecycleId}
                onChange={setAutoGenerateRecycleId}
                checkedChildren="自动生成"
                unCheckedChildren="手动输入"
              />
            </Form.Item>

            {!autoGenerateRecycleId && (
              <Form.Item name="itemId" label="回收物品ID" rules={[{ required: true, message: '请输入回收物品ID' }]}>
                <Input placeholder="如 HS001、HS002" />
              </Form.Item>
            )}

            <Form.Item name="description" label="物品名称" rules={[{ required: true, message: '请输入物品名称' }]}>
              <Input placeholder="物品在游戏中显示的名称" />
            </Form.Item>

            <Form.Item name="blueprint" label="蓝图路径" rules={[{ required: true, message: '请选择或输入蓝图路径' }]}>
              <ARKItemSelector
                placeholder="选择物品或输入蓝图路径"
                onChange={(value) => {
                  recycleForm.setFieldsValue({ blueprint: value });
                }}
              />
            </Form.Item>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="price" label="回收价格(金币)" rules={[{ required: true, message: '请输入回收价格' }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="amount" label="数量" rules={[{ required: true, message: '请输入数量' }]} initialValue={1}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="itemType" label="物品类型" initialValue="item">
              <Select placeholder="选择物品类型">
                <Option value="item">物品</Option>
                <Option value="resource">资源</Option>
                <Option value="tool">工具</Option>
                <Option value="weapon">武器</Option>
                <Option value="armor">护甲</Option>
              </Select>
            </Form.Item>

            <Divider />
            <Space>
              <Button type="primary" htmlType="submit">保存回收物品</Button>
              <Button
                onClick={() => {
                  recycleForm.resetFields();
                }}
              >
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Modal>

      {/* 添加礼包模态框 */}
      <Modal
        title="添加礼包"
        open={addKitModalVisible}
        onCancel={() => {
          setAddKitModalVisible(false);
          kitsForm.resetFields();
          setKitItemsState([]);
          setKitDinosState([]);
        }}
        onOk={() => kitsForm.submit()}
        width={1200}
      >
        <Card
          title="新增礼包（Kits）"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="参数说明"
            description={
              <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                <div><strong>礼包ID</strong>：可选择自动生成（如 K001、K002...）或手动输入自定义ID。</div>
                <div><strong>描述</strong>：礼包的展示名称。</div>
                <div><strong>默认数量(DefaultAmount)</strong>：每次领取或购买发放的次数。</div>
                <div><strong>价格(Price)</strong>：金币价格；若为VIP礼包可仅设置权限无需价格。</div>
                <div><strong>积分(Points)</strong>：可选，某些配置下可能使用。</div>
                <div><strong>权限(Permissions)</strong>：限制可领取/购买的用户组（逗号分隔），留空代表不限。</div>
                <div><strong>等级限制</strong>：MinLevel / MaxLevel；不填则默认 1 / 500 或按需设置。</div>
                <div><strong>只在出生地有效(OnlyFromSpawn)</strong>：新手礼包一般开启。</div>
                <div><strong>Items</strong>（物品）：包含 <em>Amount / Blueprint / Quality / ForceBlueprint</em>，可选 <em>Fixed / Durability / Armor / Damage</em> 等。</div>
                <div><strong>Dinos</strong>（恐龙）：包含 <em>Blueprint / Level</em>，可选 <em>Gender / Neutered / SaddleBlueprint / PreventCryo</em> 等。</div>
              </div>
            }
          />

          <Form
            form={kitsForm}
            layout="vertical"
            onFinish={async (values) => {
              try {
                let kitId;
                
                if (autoGenerateKitId) {
                  // 自动生成唯一的礼包ID
                  kitId = generateUniqueKitId();
                } else {
                  // 使用手动输入的礼包ID
                  kitId = values.kitId;
                  if (!kitId || !kitId.trim()) {
                    message.error('请输入礼包ID');
                    return;
                  }
                  
                  // 检查ID是否已存在
                  const existingIds = new Set([
                    ...Object.keys(shopConfig.ShopItems || {}),
                    ...Object.keys(shopConfig.SellItems || {}),
                    ...Object.keys(shopConfig.Kits || {})
                  ]);
                  
                  if (existingIds.has(kitId)) {
                    message.error(`礼包ID "${kitId}" 已存在，请使用其他ID`);
                    return;
                  }
                }

                const newKit: any = {
                  Description: values.description || '',
                  DefaultAmount: values.defaultAmount || 1,
                  Items: kitItemsState,
                  Dinos: kitDinosState,
                };

                if (values.price != null) newKit.Price = values.price;
                if (values.points != null) newKit.Points = values.points;
                if (values.permissions && values.permissions.length > 0) {
                  newKit.Permissions = values.permissions.join(',');
                } else {
                  newKit.Permissions = '';
                }
                if (values.minLevel != null) newKit.MinLevel = values.minLevel;
                if (values.maxLevel != null) newKit.MaxLevel = values.maxLevel;
                if (typeof values.onlyFromSpawn === 'boolean') newKit.OnlyFromSpawn = values.onlyFromSpawn;

                const newConfig = { ...(shopConfig || {}) } as any;
                if (!newConfig.Kits) newConfig.Kits = {};
                newConfig.Kits[kitId] = newKit;

                await updateShopConfig(newConfig);
                message.success(`礼包 ${kitId} 已添加${autoGenerateKitId ? '（ID自动生成）' : ''}`);
                kitsForm.resetFields();
                setKitItemsState([]);
                setKitDinosState([]);
                setAddKitModalVisible(false);
              } catch (e) {
                console.error(e);
                message.error('新增礼包失败');
              }
            }}
          >

            <Form.Item label="礼包ID生成方式">
              <Switch
                checked={autoGenerateKitId}
                onChange={setAutoGenerateKitId}
                checkedChildren="自动生成"
                unCheckedChildren="手动输入"
              />
            </Form.Item>

            {!autoGenerateKitId && (
              <Form.Item name="kitId" label="礼包ID" rules={[{ required: true, message: '请输入礼包ID' }]}>
                <Input placeholder="如 A1 / VIP1 / Z001" />
              </Form.Item>
            )}

            <Form.Item name="description" label="描述" rules={[{ required: true, message: '请输入描述' }]}>
              <Input placeholder="礼包展示名称" />
            </Form.Item>

            <Form.Item name="defaultAmount" label="默认数量" initialValue={1}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="price" label="价格(金币)">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="points" label="积分(可选)">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="permissions" label="权限(可多选)">
              <Select mode="multiple" placeholder="不选表示不限用户组">
                {vipList
                  .filter((v: string) => v && v.trim())
                  .map((v: string) => (
                    <Option key={v} value={v}>{v}</Option>
                  ))}
              </Select>
            </Form.Item>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="minLevel" label="最低等级" initialValue={1}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxLevel" label="最高等级" initialValue={500}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="onlyFromSpawn" label="只在出生地有效" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>

            <Divider>礼包内容 - 物品</Divider>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              {/* 表头 */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1.5fr 1.5fr',
                  gap: 8,
                  fontWeight: 600,
                  padding: '8px 4px',
                  color: '#555'
                }}
              >
                <div>蓝图路径</div>
                <div>数量</div>
                <div>品质</div>
                <div>耐久</div>
                <div>护甲</div>
                <div>伤害</div>
                <div>强制蓝图</div>
                <div>固定属性</div>
              </div>

              <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="dashed"
                onClick={() => setKitItemsState([...kitItemsState, {
                  Amount: 1,
                  Blueprint: '',
                  Quality: 0,
                  ForceBlueprint: false,
                  Fixed: false,
                  Durability: 0,
                  Armor: 0,
                  Damage: 0
                }])}
              >
                + 添加物品条目
              </Button>
                {kitItemsState.map((it, idx) => (
                  <Card key={`kit-item-${idx}`} size="small" title={`物品 ${idx + 1}`} style={{ marginBottom: 8 }}
                    extra={
                      <Button type="text" danger size="small" onClick={() => {
                        const arr = kitItemsState.filter((_, i) => i !== idx);
                        setKitItemsState(arr);
                      }}>删除</Button>
                    }
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1.5fr 1.5fr',
                        gap: 8,
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <ARKItemSelector
                          value={it.Blueprint}
                          placeholder="蓝图路径(Blueprint)"
                          onChange={(value) => {
                            const arr = [...kitItemsState];
                            arr[idx] = { ...arr[idx], Blueprint: value };
                            setKitItemsState(arr);
                          }}
                        />
                      </div>
                      <InputNumber min={1} placeholder="数量" value={it.Amount}
                        onChange={(v) => {
                          const arr = [...kitItemsState];
                          arr[idx] = { ...arr[idx], Amount: v };
                          setKitItemsState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber min={0} max={100} placeholder="品质"
                        value={it.Quality}
                        onChange={(v) => {
                          const arr = [...kitItemsState];
                          arr[idx] = { ...arr[idx], Quality: v };
                          setKitItemsState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber min={0} placeholder="耐久" value={it.Durability}
                        onChange={(v) => {
                          const arr = [...kitItemsState];
                          arr[idx] = { ...arr[idx], Durability: v };
                          setKitItemsState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber min={0} placeholder="护甲" value={it.Armor}
                        onChange={(v) => {
                          const arr = [...kitItemsState];
                          arr[idx] = { ...arr[idx], Armor: v };
                          setKitItemsState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber min={0} placeholder="伤害" value={it.Damage}
                        onChange={(v) => {
                          const arr = [...kitItemsState];
                          arr[idx] = { ...arr[idx], Damage: v };
                          setKitItemsState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <div>
                        <Switch
                          checked={!!it.ForceBlueprint}
                          onChange={(v) => {
                            const arr = [...kitItemsState];
                            arr[idx] = { ...arr[idx], ForceBlueprint: v };
                            setKitItemsState(arr);
                          }}
                        /> 强制蓝图
                      </div>
                      <div>
                        <Switch
                          checked={!!it.Fixed}
                          onChange={(v) => {
                            const arr = [...kitItemsState];
                            arr[idx] = { ...arr[idx], Fixed: v };
                            setKitItemsState(arr);
                          }}
                        /> 固定属性
                      </div>
                    </div>
                  </Card>
                ))}
              </Space>
            </div>

            <Divider>礼包内容 - 恐龙</Divider>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              {/* 表头 */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr 1fr',
                  gap: 8,
                  fontWeight: 600,
                  padding: '8px 4px',
                  color: '#555'
                }}
              >
                <div>蓝图路径</div>
                <div>等级</div>
                <div>性别</div>
                <div>鞍具蓝图</div>
                <div>绝育</div>
                <div>禁止低温</div>
              </div>

              <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="dashed"
                onClick={() => setKitDinosState([...kitDinosState, {
                  Blueprint: '',
                  Level: 1,
                  Gender: 'random',
                  Neutered: false,
                  PreventCryo: false,
                  SaddleBlueprint: ''
                }])}
              >
                + 添加恐龙条目
              </Button>
                {kitDinosState.map((dn, idx) => (
                  <Card key={`kit-dino-${idx}`} size="small" title={`恐龙 ${idx + 1}`} style={{ marginBottom: 8 }}
                    extra={
                      <Button type="text" danger size="small" onClick={() => {
                        const arr = kitDinosState.filter((_, i) => i !== idx);
                        setKitDinosState(arr);
                      }}>删除</Button>
                    }
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr 1fr',
                        gap: 8,
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <ARKItemSelector
                          value={dn.Blueprint}
                          placeholder="蓝图路径(Blueprint)"
                          onChange={(value) => {
                            const arr = [...kitDinosState];
                            arr[idx] = { ...arr[idx], Blueprint: value };
                            setKitDinosState(arr);
                          }}
                        />
                      </div>
                      <InputNumber min={1} placeholder="等级(Level)" value={dn.Level}
                        onChange={(v) => {
                          const arr = [...kitDinosState];
                          arr[idx] = { ...arr[idx], Level: v };
                          setKitDinosState(arr);
                        }}
                        style={{ width: '100%' }}
                      />
                      <Select
                        value={dn.Gender}
                        onChange={(v) => {
                          const arr = [...kitDinosState];
                          arr[idx] = { ...arr[idx], Gender: v };
                          setKitDinosState(arr);
                        }}
                        style={{ width: '100%' }}
                      >
                        <Option value="random">随机</Option>
                        <Option value="male">雄性</Option>
                        <Option value="female">雌性</Option>
                      </Select>
                      <div>
                        <ARKItemSelector
                          value={dn.SaddleBlueprint}
                          placeholder="鞍具蓝图(SaddleBlueprint，可选)"
                          onChange={(value) => {
                            const arr = [...kitDinosState];
                            arr[idx] = { ...arr[idx], SaddleBlueprint: value };
                            setKitDinosState(arr);
                          }}
                        />
                      </div>
                      <div>
                        <Switch
                          checked={!!dn.Neutered}
                          onChange={(v) => {
                            const arr = [...kitDinosState];
                            arr[idx] = { ...arr[idx], Neutered: v };
                            setKitDinosState(arr);
                          }}
                        /> 绝育
                      </div>
                      <div>
                        <Switch
                          checked={!!dn.PreventCryo}
                          onChange={(v) => {
                            const arr = [...kitDinosState];
                            arr[idx] = { ...arr[idx], PreventCryo: v };
                            setKitDinosState(arr);
                          }}
                        /> 禁止低温
                      </div>
                    </div>
                  </Card>
                ))}
              </Space>
            </div>

            <Divider />
            <Space>
              <Button type="primary" htmlType="submit">保存礼包</Button>
              <Button
                onClick={() => {
                  kitsForm.resetFields();
                  setKitItemsState([]);
                  setKitDinosState([]);
                }}
              >
                重置
              </Button>
            </Space>
          </Form>
        </Card>
      </Modal>

    </div>
  );
};

export default ShopManagement;
